import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Box, Button, TextField, Typography, Paper, InputAdornment, Alert, Container, CircularProgress, keyframes } from '@mui/material';
import { MailIcon, ArrowLeftIcon, UserIcon, HomeIcon, CheckCircleIcon } from 'lucide-react';
// Define keyframes for animations
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;
const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [rippleEffect, setRippleEffect] = useState(false);
  const {
    requestPasswordReset,
    verifyOtp
  } = useAuth();
  const navigate = useNavigate();
  // Validate form on input change
  useEffect(() => {
    if (isOtpSent) {
      setFormValid(!!otp && otp.length === 6);
    } else {
      const emailValid = validateEmail(email);
      const usernameValid = !!username;
      const branchCodeValid = branchCode.length >= 3 && branchCode.length <= 10;
      setFormValid(!emailValid && usernameValid && branchCodeValid);
    }
  }, [email, username, branchCode, otp, isOtpSent]);
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!re.test(email)) return 'Please enter a valid email';
    return '';
  };
  const validateBranchCode = (value: string) => {
    if (!value) return 'Branch code is required';
    if (!/^\d{4}$/.test(value)) {
      return 'Branch code must be exactly 4 digits';
    }
    return '';
  };
  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    if (!username) {
      setError('Username is required');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    const branchCodeError = validateBranchCode(branchCode);
    if (branchCodeError) {
      setError(branchCodeError);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    if (branchCode.length < 3 || branchCode.length > 10) {
      setError('Branch code must be between 3-10 characters');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    setIsLoading(true);
    setRippleEffect(true);
    try {
      // For now, we're only passing email to the mock function
      // In a real app, we would pass username and branchCode as well
      const success = await requestPasswordReset(email);
      if (success) {
        setIsOtpSent(true);
        setError('');
        setSuccess('A one-time password has been sent to your registered email address.');
      } else {
        setError('Failed to send OTP. Please verify your details and try again.');
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }
    } catch (error) {
      setError('An error occurred. Please try again later.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    } finally {
      setIsLoading(false);
      setRippleEffect(false);
    }
  };
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp) {
      setError('OTP is required');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    if (otp.length !== 6 || !/^\d+$/.test(otp)) {
      setError('OTP must be 6 digits');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return;
    }
    setIsLoading(true);
    setRippleEffect(true);
    try {
      const success = await verifyOtp(otp);
      if (success) {
        navigate('/reset-password');
      } else {
        setError('Invalid OTP. Please try again.');
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }
    } catch (error) {
      setError('An error occurred. Please try again later.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    } finally {
      setIsLoading(false);
      setRippleEffect(false);
    }
  };
  return <Box sx={{
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    position: 'relative',
    px: 2,
    overflow: 'hidden'
  }} data-id="element-135">
      <Container maxWidth="sm" sx={{
      mb: 4,
      mt: 4,
      position: 'relative',
      zIndex: 1
    }} data-id="element-136">
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 3
      }} data-id="element-137">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xyh4FinweYvNRM6XVnTFSv/openRemitLogo-1.png" alt="OpenRemit Logo" style={{
          height: 80,
          marginBottom: 20
        }} data-id="element-138" />
          <Typography variant="subtitle1" sx={{
          color: '#6B7280',
          textAlign: 'center',
          fontWeight: 600,
          letterSpacing: '1px'
        }} data-id="element-139">
            Account Recovery
          </Typography>
        </Box>

        <Paper elevation={0} sx={{
        p: {
          xs: 3,
          sm: 4
        },
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
        position: 'relative',
        animation: isShaking ? `${shakeAnimation} 0.5s ease-in-out` : 'none'
      }} data-id="element-140">
          {isSubmitted ? (<Box sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: '#ECFDF5',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3
        }} data-id="element-141">
                <CheckCircleIcon size={40} color="#0B7923" data-id="element-142" />
              </Box>, <Typography variant="h5" component="h2" sx={{
          mb: 2,
          textAlign: 'center',
          color: '#1F2937',
          fontWeight: 700
        }} data-id="element-143">
                Check Your Email
              </Typography>, <Typography variant="body2" color="text.secondary" sx={{
          mb: 3,
          textAlign: 'center'
        }} data-id="element-144">
                We've sent a one-time password (OTP) to your registered email
                address. Please check your inbox and follow the instructions to
                reset your password.
              </Typography>, <Button component={Link} to="/login" startIcon={<ArrowLeftIcon size={16} data-id="element-146" />} sx={{
          color: '#6B7280',
          textTransform: 'none',
          fontWeight: 500,
          '&:hover': {
            backgroundColor: 'transparent',
            color: '#0052CC'
          }
        }} data-id="element-145">
                Return to Login
              </Button>) : <Typography variant="h5" component="h2" sx={{
          mb: 1,
          textAlign: 'center',
          color: '#1F2937',
          fontWeight: 700
        }} data-id="element-147">
              Forgot Password
            </Typography>}
          {!isOtpSent ? <Typography variant="body2" color="text.secondary" sx={{
          mb: 3,
          textAlign: 'center'
        }} data-id="element-148">
              Enter your registered details to receive a one-time password (OTP)
              by email.
            </Typography> : <Typography variant="body2" color="text.secondary" sx={{
          mb: 3,
          textAlign: 'center',
          animation: `${slideIn} 0.5s ease-out forwards`
        }} data-id="element-149">
              Enter the 6-digit code sent to your email.
            </Typography>}
          {error && <Alert severity="error" sx={{
          mb: 3,
          borderRadius: '12px',
          border: '1px solid rgba(255, 93, 143, 0.3)',
          animation: isShaking ? `${shakeAnimation} 0.5s ease-in-out` : 'none'
        }} data-id="element-150">
              {error}
            </Alert>}
          {success && <Alert severity="success" icon={<CheckCircleIcon size={20} color="#0B7923" data-id="element-152" />} sx={{
          mb: 3,
          backgroundColor: '#ECFDF5',
          border: '1px solid #0B7923',
          color: '#047857'
        }} data-id="element-151">
              {success}
            </Alert>}
          {!isOtpSent ? <form onSubmit={handleRequestOtp} data-id="element-153">
              <TextField id="username" label="Username" variant="outlined" fullWidth value={username} onChange={e => setUsername(e.target.value)} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-155">
                      <UserIcon size={20} color="#0052CC" data-id="element-156" />
                    </InputAdornment>
          }} sx={{
            mb: 2.5
          }} data-id="element-154" />

              <TextField id="email" label="Email Address" variant="outlined" fullWidth value={email} onChange={e => setEmail(e.target.value)} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-158">
                      <MailIcon size={20} color="#0052CC" data-id="element-159" />
                    </InputAdornment>
          }} sx={{
            mb: 2.5
          }} data-id="element-157" />

              <TextField id="branchCode" label="Branch Code" variant="outlined" fullWidth value={branchCode} onChange={e => {
            const value = e.target.value.replace(/\D/g, '');
            if (value.length <= 4) setBranchCode(value);
          }} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-161">
                      <HomeIcon size={20} color="#0052CC" data-id="element-162" />
                    </InputAdornment>
          }} inputProps={{
            maxLength: 4,
            pattern: '[0-9]{4}'
          }} helperText="4-digit branch code (e.g., 0001)" sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused': {
                boxShadow: '0 0 0 4px rgba(0, 180, 216, 0.15)'
              }
            }
          }} data-id="element-160" />
              <Box sx={{
            position: 'relative',
            overflow: 'hidden'
          }} data-id="element-163">
                <Button type="submit" variant="contained" color="primary" fullWidth disabled={isLoading || !formValid} sx={{
              py: 1.5,
              mb: 3,
              borderRadius: '6px',
              backgroundColor: '#0052CC',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#003DA8',
                boxShadow: '0px 1px 3px rgba(0,0,0,0.12)'
              }
            }} data-id="element-164">
                  {isLoading ? <CircularProgress size={24} color="inherit" sx={{
                color: 'white',
                animation: `${glowPulse} 1.5s infinite ease-in-out`
              }} data-id="element-165" /> : 'Send OTP'}
                </Button>
                {rippleEffect && <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.5)',
                animation: `${rippleAnimation} 1s forwards`
              }
            }} data-id="element-166" />}
              </Box>
            </form> : <form onSubmit={handleVerifyOtp} style={{
          animation: `${slideIn} 0.5s ease-out forwards`
        }} data-id="element-167">
              <TextField id="otp" label="One-Time Password" variant="outlined" fullWidth value={otp} onChange={e => setOtp(e.target.value)} inputProps={{
            maxLength: 6
          }} placeholder="6-digit OTP" sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused': {
                boxShadow: '0 0 0 4px rgba(0, 180, 216, 0.15)'
              }
            },
            '& input': {
              letterSpacing: '0.25em',
              fontSize: '1.1rem',
              fontWeight: '600'
            }
          }} data-id="element-168" />
              <Box sx={{
            position: 'relative',
            overflow: 'hidden'
          }} data-id="element-169">
                <Button type="submit" variant="contained" color="primary" fullWidth disabled={isLoading || !formValid} sx={{
              py: 1.5,
              mb: 3,
              borderRadius: '6px',
              backgroundColor: '#0052CC',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#003DA8',
                boxShadow: '0px 1px 3px rgba(0,0,0,0.12)'
              }
            }} data-id="element-170">
                  {isLoading ? <CircularProgress size={24} color="inherit" sx={{
                color: 'white',
                animation: `${glowPulse} 1.5s infinite ease-in-out`
              }} data-id="element-171" /> : 'Verify OTP'}
                </Button>
                {rippleEffect && <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.5)',
                animation: `${rippleAnimation} 1s forwards`
              }
            }} data-id="element-172" />}
              </Box>
            </form>}
          <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 2
        }} data-id="element-173">
            <Link to="/login" style={{
            textDecoration: 'none'
          }} data-id="element-174">
              <Button startIcon={<ArrowLeftIcon size={16} data-id="element-176" />} sx={{
              color: '#6B7280',
              transition: 'all 0.2s ease',
              fontWeight: 500,
              '&:hover': {
                background: 'rgba(0, 119, 182, 0.1)',
                color: '#00B4D8',
                transform: 'translateY(-1px)'
              }
            }} data-id="element-175">
                Back to Login
              </Button>
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>;
};
export default ForgotPasswordPage;